import { type Placement } from 'tippy.js'
import type { UiMenuTriggerTheme } from '../UiMenu/types'
import type defaultProps from './defaultProps'

export enum Modes {
  Single = 'single',
  Multiple = 'multiple',
}

export enum SelectAllModes {
  All = 'all',
  IntermediaryAll = 'intermediaryAll',
  None = 'none',
  Tags = 'tags',
}

export enum DisplayModes {
  List = 'list',
  Tags = 'tags',
}

export interface DropdownProps<
  T extends object,
  P extends keyof T,
  L extends keyof T,
  M extends Modes,
  V extends T,
  O extends boolean = false,
> {
  mode: M
  isObject?: O
  propToCheck: P
  options?: T[]
  modelValue: O extends true ? V[] : V[P][]
  closeOnSelect?: boolean
  selectAllLabel?: string
  allowSingleDeselect?: boolean
  allowDeselectAll?: boolean
  maxHeight?: number
  triggerLabel?: string
  triggerPlaceholder?: string
  label: L
  searchPlaceholder?: string
  noResultsText?: string
  loadingText?: string
  selectAllMode?: SelectAllModes
  filterResults?: boolean
  total?: number
  maxWidth?: number
  fullWidth?: boolean
  isLoading?: boolean
  reorderAfterSelect?: boolean
  hasSearch?: boolean
  itemsLabel?: string
  size?: string
  appendToDocument?: boolean
  disabled?: boolean
  excludedOptions?: O extends true ? V[] : V[P][]
  canRemove?: boolean
  triggerTheme?: UiMenuTriggerTheme
  groups?: OptionGroup<T>[]
  placement?: Placement
  display?: DisplayModes
  helpText?: string
  error?: boolean
  generalCreateOptions?: (Record<string, any> & { key?: string, testId: string,onClick: () => void })[]
}

type Defaults = typeof defaultProps

declare type NotUndefined<T> = T extends undefined ? never : T

export type PropsWithDefaults<
  T extends object,
  P extends keyof T,
  L extends keyof T,
  M extends Modes,
  V extends T,
  O extends boolean,
> = DropdownProps<T, P, L, M, V, O> & {
  [K in keyof Defaults]: K extends keyof DropdownProps<T, P, L, M, V, O>
    ? Defaults[K] extends undefined
      ? DropdownProps<T, P, L, M, V, O>[K]
      : NotUndefined<DropdownProps<T, P, L, M, V, O>[K]>
    : never
}

export type Selected<T extends object, P extends keyof T, M extends Modes, O extends boolean> = O extends true
  ? M extends typeof Modes.Single
    ? T
    : T[]
  : M extends typeof Modes.Single
    ? T[P]
    : T[P][]

export type Value<T extends object, P extends keyof T, O extends boolean> = O extends true ? T : T[P]

export interface UpdateParams<T extends object, P extends keyof T, M extends Modes, O extends boolean> {
  selected: Selected<T, P, M, O>
  excluded?: Selected<T, P, M, O>
}

export interface OptionGroup<T> {
  id?: string
  name?: string
  options: T[]
  total?: number
  emptyText?: string
}

export type AllOption = Record<string, string | number>
